import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Space, Typography, Modal, Input, notification, Alert } from 'antd';
import { CheckOutlined, CloseOutlined, UndoOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';

const { Text } = Typography;

/**
 * Decision toolbar for the claim's line items.
 *
 * Deliberately not a table: the adjudication screen already lists every line
 * with its filed / PA / claims amounts. A second table of the same lines would
 * show the money in one copy and the checkboxes in another. So the marking lives
 * on the existing rows and this supplies the actions above them.
 *
 * Marking is staged and reversible; nothing moves until Apply. claim_flag is what
 * the payable totals read, so writing a decision on every tick would move the
 * facility's payable amount while the reviewer is still working.
 *
 * Owns the decision state because the rows come from the legacy PAListAdminClaim
 * payload, which carries no claim_mark / claim_decidable. It fetches them from
 * v2 and hands the parent a map to merge, rather than every row fetching alone.
 */
export default function ClaimLineDecisions({
  checkinId,
  selectedIds = [],
  onClearSelection = () => {},
  onStateChange = () => {},
  onApplied,
  canMark = true,
  canApply = true,
}) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectScope, setRejectScope] = useState('selection');
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    if (!checkinId) return;
    try {
      const [itemsResp, summaryResp] = await Promise.all([
        lib.getClaimItemsV2(checkinId),
        lib.getClaimLineDecisions(checkinId),
      ]);

      const parsedItems = lib.parseApiEnvelope(itemsResp);
      const map = {};
      if (parsedItems.ok && Array.isArray(parsedItems.data)) {
        parsedItems.data.forEach((row) => {
          const id = row?.id_ ?? row?.id;
          if (id != null) {
            map[String(id)] = {
              claim_mark: Number(row.claim_mark || 0),
              claim_mark_comment: row.claim_mark_comment || null,
              claim_marked_by_name: row.claim_marked_by_name || null,
              claim_decision: Number(row.claim_decision || 0),
              claim_decidable: Boolean(row.claim_decidable),
            };
          }
        });
      }

      const parsedSummary = lib.parseApiEnvelope(summaryResp);
      let nextSummary = null;
      if (parsedSummary.ok) {
        nextSummary = parsedSummary.data?.summary || null;
        setUnavailable(parsedSummary.data?.marks_supported === false);
      } else if (parsedSummary.code === 'CLAIM_DECISIONS_UNAVAILABLE') {
        setUnavailable(true);
      }

      setSummary(nextSummary);
      // Rows and counts land together; a header that contradicts the list under
      // it is worse than a slower refresh.
      onStateChange({ decisions: map, summary: nextSummary });
    } catch (e) {
      // A failed refresh must not blank the list the reviewer is reading.
    }
  }, [checkinId, onStateChange]);

  useEffect(() => { load(); }, [load]);

  const markedCount = (summary?.marked_accept || 0) + (summary?.marked_reject || 0);
  const decidable = summary?.decidable || 0;
  const disabled = !canMark || unavailable || Boolean(busy);

  const afterWrite = useCallback(async (parsed, fallback) => {
    if (parsed.ok) {
      notification.success({ message: parsed.message || fallback });
      onClearSelection();
    } else if (parsed.code === 'CLAIM_DECISIONS_UNAVAILABLE') {
      setUnavailable(true);
      notification.warning({
        message: 'Line decisions are not enabled on this environment yet',
        description: 'The claim decision migration has not been applied here.',
      });
    } else {
      notification.error({ message: parsed.message || 'That did not go through' });
    }
    await load();
    if (parsed.ok && typeof onApplied === 'function') onApplied(parsed.data);
  }, [load, onApplied, onClearSelection]);

  const run = useCallback(async (key, fn, fallback) => {
    setBusy(key);
    try {
      await afterWrite(lib.parseApiEnvelope(await fn()), fallback);
    } catch (e) {
      notification.error({ message: e?.message || 'That did not go through' });
    } finally {
      setBusy('');
    }
  }, [afterWrite]);

  const markSelection = (decision, comment = '') =>
    run(`mark-${decision}`, () => lib.markClaimLines(checkinId, selectedIds, decision, comment), 'Lines marked');

  const markEverything = (decision, comment = '') =>
    run(`all-${decision}`, () => lib.markAllClaimLines(checkinId, decision, comment), 'All remaining lines marked');

  const clearSelection = () =>
    run('clear', () => lib.markClaimLines(checkinId, selectedIds, 'clear'), 'Marks cleared');

  // The confirm names the numbers and the consequence. "Are you sure?" tells a
  // reviewer nothing they did not already know.
  const confirmApply = useCallback(() => {
    const accept = summary?.marked_accept || 0;
    const reject = summary?.marked_reject || 0;
    Modal.confirm({
      title: 'Apply these decisions?',
      okText: `Apply ${accept + reject}`,
      okButtonProps: { danger: reject > 0 },
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            {accept} line{accept === 1 ? '' : 's'} will be approved for payment
            {reject > 0 ? `, and ${reject} declined.` : '.'}
          </p>
          <Text type="secondary">
            This is what changes the payable amount on this claim. Marks can be
            cleared before applying; applied decisions cannot be undone here.
          </Text>
        </div>
      ),
      onOk: () => run('apply', () => lib.applyClaimLineDecisions(checkinId), 'Decisions applied'),
    });
  }, [checkinId, summary, run]);

  const submitReject = useCallback(async () => {
    const reason = rejectReason.trim();
    setRejectOpen(false);
    if (rejectScope === 'all') await markEverything('reject', reason);
    else await markSelection('reject', reason);
    setRejectReason('');
  }, [rejectReason, rejectScope]); // eslint-disable-line react-hooks/exhaustive-deps

  if (unavailable) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Line-by-line decisions are not enabled here yet"
        description="The claim decision migration has not run on this environment, so these lines are read-only."
      />
    );
  }

  if (!canMark && !canApply) return null;

  const n = selectedIds.length;

  return (
    <div className="claims-decision-bar">
      <div className="claims-decision-bar__counts">
        <Text className="claims-decision-bar__selected">
          {n ? `${n} selected` : `${decidable} line${decidable === 1 ? '' : 's'} awaiting decision`}
        </Text>
        {summary?.marked_accept ? (
          <span className="claims-decision-chip claims-decision-chip--accept">
            {summary.marked_accept} to accept
          </span>
        ) : null}
        {summary?.marked_reject ? (
          <span className="claims-decision-chip claims-decision-chip--reject">
            {summary.marked_reject} to reject
          </span>
        ) : null}
        {summary?.not_administered ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {summary.not_administered} not administered
          </Text>
        ) : null}
      </div>

      <Space wrap size={8}>
        <Button
          size="small"
          icon={<CheckOutlined />}
          disabled={disabled || !n}
          loading={busy === 'mark-accept'}
          onClick={() => markSelection('accept')}
        >
          Accept selected
        </Button>
        <Button
          size="small"
          danger
          icon={<CloseOutlined />}
          disabled={disabled || !n}
          loading={busy === 'mark-reject'}
          onClick={() => { setRejectScope('selection'); setRejectOpen(true); }}
        >
          Reject selected
        </Button>
        <Button
          size="small"
          disabled={disabled || !n}
          icon={<UndoOutlined />}
          loading={busy === 'clear'}
          onClick={clearSelection}
        >
          Clear
        </Button>
        <span className="claims-decision-bar__divider" />
        <Button
          size="small"
          disabled={disabled || !decidable}
          loading={busy === 'all-accept'}
          onClick={() => markEverything('accept')}
        >
          Accept all
        </Button>
        <Button
          size="small"
          danger
          disabled={disabled || !decidable}
          loading={busy === 'all-reject'}
          onClick={() => { setRejectScope('all'); setRejectOpen(true); }}
        >
          Reject all
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!canApply || unavailable || Boolean(busy) || markedCount === 0}
          loading={busy === 'apply'}
          onClick={confirmApply}
        >
          {markedCount ? `Apply ${markedCount}` : 'Apply'}
        </Button>
      </Space>

      <Modal
        open={rejectOpen}
        title={rejectScope === 'all' ? 'Reject every remaining line' : `Reject ${n} line${n === 1 ? '' : 's'}`}
        okText="Mark to reject"
        okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
        onOk={submitReject}
        onCancel={() => { setRejectOpen(false); setRejectReason(''); }}
      >
        <p>
          A declined line is paid nothing. The reason is stored against the line
          and is what the facility is told, so it cannot be left blank.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={500}
          value={rejectReason}
          placeholder="Why is this not payable?"
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
